import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { Language } from '@/types';
import { Menu, X, ArrowRight, ExternalLink, Globe } from 'lucide-react';

export interface AgroScoreNavItem {
  id: string;
  labelEn: string;
  labelAr: string;
  href: string;
  isExternal?: boolean;
}

export const AGROSCORE_NAV_ITEMS: AgroScoreNavItem[] = [
  { id: 'overview', labelEn: 'Overview', labelAr: 'نظرة عامة', href: '/products/agroscore' },
  { id: 'grow', labelEn: 'Grow', labelAr: 'تزرع', href: '/products/agroscore/grow' },
  { id: 'finance', labelEn: 'Finance', labelAr: 'تموّل', href: '/products/agroscore/finance' },
  { id: 'govern', labelEn: 'Govern', labelAr: 'تدير', href: '/products/agroscore/govern' }
];

interface AgroScoreNavbarProps {
  lang: Language;
  setLang?: (lang: Language) => void;
}

export const AgroScoreNavbar: React.FC<AgroScoreNavbarProps> = ({ lang, setLang }) => {
  const isAr = lang === 'ar';
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Track scroll position for subtle background opacity and telemetry accent
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile drawer on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const toggleLanguage = () => {
    if (setLang) {
      setLang(isAr ? 'en' : 'ar');
    }
  };

  return (
    <>
      <motion.nav
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        dir="ltr"
        className={`
          fixed top-0 left-0 right-0 z-50 
          h-14 md:h-16 
          flex items-center 
          transition-colors duration-200 ease-out
          border-b
          ${
            scrolled
              ? 'bg-[#05070A]/90 backdrop-blur-xl border-[#262C31] shadow-[0_4px_30px_rgba(0,0,0,0.6)]'
              : 'bg-[#05070A]/75 backdrop-blur-md border-[#262C31]/70 shadow-[0_2px_15px_rgba(0,0,0,0.3)]'
          }
        `}
      >
        <div className="w-full max-w-[1450px] mx-auto px-4 sm:px-6 flex justify-between items-center">
          
          {/* ═══ Left Section: Brand Logo (VEXL AgroScore) ═══ */}
          <div className="flex items-center gap-4 lg:gap-8">
            <Link
              to="/products/agroscore"
              onClick={() => {
                window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
                document.documentElement.scrollTop = 0;
                document.body.scrollTop = 0;
                if (location.pathname === '/products/agroscore' || location.pathname.startsWith('/agroscore')) {
                  if (location.hash) {
                    window.history.pushState(null, '', '/products/agroscore');
                  }
                  window.dispatchEvent(new CustomEvent('vexl:agroscore:scrollToOverview'));
                }
              }}
              className="relative z-50 group select-none"
            >
              <div className="relative flex items-center gap-2.5" dir="ltr">
                
                {/* Signature AgroScore Kinetic Telemetry Bar */}
                <motion.div
                  className="w-1.5 h-7 md:h-8 rounded-sm overflow-hidden relative"
                  animate={
                    scrolled
                      ? {
                          scaleY: [1, 1.25, 0.95, 1],
                          boxShadow: '0 0 14px rgba(16, 185, 129, 0.85)',
                        }
                      : {
                          scaleY: 1,
                          boxShadow: '0 0 4px rgba(16, 185, 129, 0.3)',
                        }
                  }
                  transition={{
                    duration: 0.45,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-b from-emerald-400 via-teal-400 to-[#0E93A3]" />
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-b from-transparent via-white to-transparent"
                    animate={
                      scrolled
                        ? { y: ['-100%', '100%'], opacity: [0, 0.9, 0] }
                        : { y: '-100%', opacity: 0 }
                    }
                    transition={{ duration: 0.4, ease: 'easeOut' }}
                  />
                </motion.div>

                {/* VEXL Typography (Crisp White) */}
                <span className="text-2xl md:text-[1.75rem] font-black tracking-[0.2em] text-white m-0 leading-none block origin-center">
                  VEXL
                </span>

                {/* AgroScore Brand (Clean ink-primary, no TM) */}
                <div className="flex items-center gap-1.5 ml-1 sm:ml-1.5">
                  <span className="text-xl md:text-2xl font-black tracking-tight text-[#EDF0F1] leading-none">
                    AgroScore
                  </span>
                </div>

              </div>
            </Link>

            {/* ═══ Desktop Navigation Links (Custom AgroScore Tabs) ═══ */}
            <div className="hidden xl:flex items-center gap-1 select-none">
              {AGROSCORE_NAV_ITEMS.map((item) => {
                const isActive = item.href === '/products/agroscore'
                  ? location.pathname === '/products/agroscore' && !location.hash
                  : location.pathname === item.href;
                return item.isExternal ? (
                  <a
                    key={item.id}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="
                      relative py-2 px-3 text-[11px] font-bold tracking-[0.15em] uppercase
                      text-[#9AA5AC] hover:text-emerald-400 transition-colors duration-150
                      flex items-center gap-1 group
                    "
                  >
                    <span>{isAr ? item.labelAr : item.labelEn}</span>
                    <ExternalLink className="w-3 h-3 opacity-60 group-hover:opacity-100 transition-opacity" />
                  </a>
                ) : (
                  <Link
                    key={item.id}
                    to={item.href}
                    onClick={() => {
                      if (item.id === 'overview' && (location.pathname.startsWith('/products/agroscore') || location.pathname.startsWith('/agroscore'))) {
                        if (location.hash) {
                          window.history.pushState(null, '', location.pathname);
                        }
                        window.dispatchEvent(new CustomEvent('vexl:agroscore:scrollToOverview'));
                      }
                      window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
                      document.documentElement.scrollTop = 0;
                      document.body.scrollTop = 0;
                    }}
                    className={`
                      relative py-2 px-3 text-[11px] font-bold tracking-[0.15em] uppercase
                      transition-colors duration-150
                      ${isActive ? 'text-white' : 'text-[#9AA5AC] hover:text-emerald-400'}
                    `}
                  >
                    <span>{isAr ? item.labelAr : item.labelEn}</span>
                    <AnimatePresence>
                      {isActive && (
                        <motion.div
                          key={`nav-active-${item.id}`}
                          initial={{ scaleX: 0.4, opacity: 0 }}
                          animate={{ scaleX: 1, opacity: 1 }}
                          exit={{ scaleX: 0.4, opacity: 0 }}
                          transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                          className="absolute bottom-0 left-3 right-3 h-[2px] bg-emerald-400 rounded-full shadow-[0_0_8px_rgba(16,185,129,0.8)] origin-center pointer-events-none"
                        />
                      )}
                    </AnimatePresence>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* ═══ Right Section: Language Switcher, Workspace CTA & Mobile Menu Toggle ═══ */}
          <div className="flex items-center gap-2.5 sm:gap-3.5">
            
            {/* Language Switcher */}
            {setLang && (
              <button
                onClick={toggleLanguage}
                className="
                  inline-flex items-center gap-1.5
                  px-3 py-1.5 rounded-full
                  bg-[#12161A] hover:bg-[#171C21]
                  border border-[#262C31] hover:border-emerald-500/50
                  text-[#CAD1D8] hover:text-white
                  text-xs font-mono font-medium
                  transition-all duration-200
                  active:scale-95 select-none
                "
                title={isAr ? 'Switch to English' : 'التحويل إلى العربية'}
              >
                <Globe size={13} className="text-emerald-400" />
                <span>{isAr ? 'EN' : 'العربية'}</span>
              </button>
            )}

            {/* Launch Workspace CTA */}
            <Link
              to="/products/agroscore/workspace"
              className="
                hidden sm:inline-flex items-center gap-1.5
                px-5 py-2 rounded-full
                bg-[#0E93A3] hover:bg-[#075C68] text-white
                text-xs font-semibold tracking-wide
                transition-all duration-200 relative z-50
                shadow-[0_0_15px_rgba(14,147,163,0.3)] hover:shadow-[0_0_24px_rgba(14,147,163,0.5)]
                active:scale-95
              "
            >
              <span>{isAr ? 'ابدأ الاستخدام' : 'Try it now'}</span>
              <ArrowRight size={13} className={isAr ? 'rotate-180' : ''} />
            </Link>

            {/* Mobile Menu Toggle Button */}
            <motion.button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="
                xl:hidden p-2.5 rounded-full
                bg-[#12161A] border border-[#262C31]
                text-[#EDF0F1] hover:border-[#0E93A3]/60
                transition-all duration-200
                relative z-[60]
              "
              aria-label="Toggle menu"
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={isMobileMenuOpen ? 'close' : 'open'}
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
                </motion.div>
              </AnimatePresence>
            </motion.button>

          </div>

        </div>
      </motion.nav>

      {/* ═══ Mobile Drawer Overlay ═══ */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="fixed inset-0 z-40 bg-[#05070A]/95 backdrop-blur-2xl pt-20 pb-10 px-6 xl:hidden overflow-y-auto flex flex-col justify-between"
            dir={isAr ? 'rtl' : 'ltr'}
          >
            {/* Top Links Section */}
            <div className="flex flex-col space-y-4 pt-4">
              <div className="flex items-center gap-2 pb-3 border-b border-[#262C31]">
                <span className="w-2 h-2 rounded-full bg-[#0E93A3]" />
                <span className="text-xs font-mono text-[#0E93A3] uppercase tracking-wider font-semibold">
                  AgroScore Navigation
                </span>
              </div>

              {AGROSCORE_NAV_ITEMS.map((item) => {
                const isActive = item.href === '/products/agroscore'
                  ? location.pathname === '/products/agroscore' && !location.hash
                  : location.pathname === item.href;
                return item.isExternal ? (
                  <a
                    key={item.id}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center justify-between py-3 text-lg font-bold text-[#CAD1D8] hover:text-[#0E93A3] border-b border-[#1A2026] transition-colors"
                  >
                    <span>{isAr ? item.labelAr : item.labelEn}</span>
                    <ExternalLink size={18} className="text-[#66707A]" />
                  </a>
                ) : (
                  <Link
                    key={item.id}
                    to={item.href}
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      if (item.id === 'overview' && (location.pathname.startsWith('/products/agroscore') || location.pathname.startsWith('/agroscore'))) {
                        if (location.hash) {
                          window.history.pushState(null, '', location.pathname);
                        }
                        window.dispatchEvent(new CustomEvent('vexl:agroscore:scrollToOverview'));
                      }
                      window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
                      document.documentElement.scrollTop = 0;
                      document.body.scrollTop = 0;
                    }}
                    className={`flex items-center justify-between py-3 text-lg font-bold border-b border-[#1A2026] transition-colors ${
                      isActive ? 'text-[#0E93A3]' : 'text-[#CAD1D8] hover:text-[#0E93A3]'
                    }`}
                  >
                    <span>{isAr ? item.labelAr : item.labelEn}</span>
                    <ArrowRight size={18} className={`text-[#66707A] ${isAr ? 'rotate-180' : ''}`} />
                  </Link>
                );
              })}
            </div>

            {/* Bottom Actions Section */}
            <div className="pt-6 space-y-4 border-t border-[#262C31]">
              <Link
                to="/products/agroscore/workspace"
                onClick={() => setIsMobileMenuOpen(false)}
                className="w-full py-3.5 rounded-xl bg-[#0E93A3] hover:bg-[#075C68] text-white text-center font-bold text-sm tracking-wide shadow-[0_0_20px_rgba(14,147,163,0.35)] flex items-center justify-center gap-2 transition-all active:scale-95"
              >
                <span>{isAr ? 'ابدأ الاستخدام الآن' : 'Try AgroScore Now'}</span>
                <ArrowRight size={16} className={isAr ? 'rotate-180' : ''} />
              </Link>

              {setLang && (
                <button
                  type="button"
                  onClick={() => {
                    toggleLanguage();
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full py-3 rounded-xl bg-[#12161A] text-[#CAD1D8] hover:text-white border border-[#262C31] text-center font-mono text-xs flex items-center justify-center gap-2 transition-colors"
                >
                  <Globe size={14} className="text-[#0E93A3]" />
                  <span>{isAr ? 'Switch to English' : 'التحويل إلى العربية'}</span>
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AgroScoreNavbar;
